import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { theme, breakpoint } from "@utilities";
import {
    ConsentCategory,
    ALL_CONSENT_CATEGORIES,
    getConsentedCategories,
    saveConsentPreferences,
    acceptAllCookies,
    acceptNecessaryOnly,
} from "src/Utilities/cookieConsent";

interface CookiePreferencesModalProps {
    onClose: () => void;
    onSave: () => void;
}

interface CategoryConfig {
    id: ConsentCategory;
    title: string;
    description: string;
    required: boolean;
}

const COOKIE_CATEGORIES: CategoryConfig[] = [
    {
        id: "necessary",
        title: "Strictly necessary cookies",
        description:
            "These cookies are essential for the website to function properly. They enable basic functions like page navigation, secure access, and shopping cart functionality. The website cannot function properly without these cookies.",
        required: true,
    },
    {
        id: "analytics",
        title: "Performance and Analytics cookies",
        description:
            "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the website's performance and user experience.",
        required: false,
    },
    {
        id: "marketing",
        title: "Advertisement and Targeting cookies",
        description:
            "These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third-party advertisers.",
        required: false,
    },
    {
        id: "preferences",
        title: "Functionality and Preferences cookies",
        description:
            "These cookies allow the website to remember choices you make (such as your username, language, or the region you are in) and provide enhanced, more personal features.",
        required: false,
    },
];

/**
 * CookiePreferencesModal Component
 *
 * A modal dialog that allows users to customize their cookie preferences.
 * Features expandable sections for each cookie category with toggle switches.
 */
export const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
    onClose,
    onSave,
}) => {
    const [preferences, setPreferences] = useState<Record<ConsentCategory, boolean>>({
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
    });
    const [expandedCategory, setExpandedCategory] = useState<ConsentCategory | null>(null);

    useEffect(() => {
        // Load existing preferences
        const existingCategories = getConsentedCategories();
        if (existingCategories.length > 0) {
            const prefs: Record<ConsentCategory, boolean> = {
                necessary: true,
                analytics: existingCategories.includes("analytics"),
                marketing:
                    existingCategories.includes("marketing") ||
                    existingCategories.includes("targeting"),
                preferences:
                    existingCategories.includes("preferences") ||
                    existingCategories.includes("functionality"),
            };
            setPreferences(prefs);
        }
    }, []);

    const handleToggle = (category: ConsentCategory) => {
        if (category === "necessary") return; // Can't disable necessary cookies

        setPreferences((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const handleToggleExpand = (category: ConsentCategory) => {
        setExpandedCategory((prev) => (prev === category ? null : category));
    };

    const handleAcceptAll = () => {
        acceptAllCookies();
        onSave();
    };

    const handleRejectAll = () => {
        acceptNecessaryOnly();
        onSave();
    };

    const handleSaveSettings = () => {
        const selectedCategories: ConsentCategory[] = ALL_CONSENT_CATEGORIES.filter(
            (cat) => preferences[cat],
        );
        saveConsentPreferences(selectedCategories);
        onSave();
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>Cookie preferences</ModalTitle>
                    <CloseButton onClick={onClose} aria-label="Close">
                        <CloseIcon>&times;</CloseIcon>
                    </CloseButton>
                </ModalHeader>

                <ModalBody>
                    <IntroSection>
                        <IntroTitle>Cookie usage</IntroTitle>
                        <IntroText>
                            We use cookies to ensure the website's proper operation, to analyze
                            traffic and performance, and to provide social media features. Click on
                            the different category headings to find out more and change our default
                            settings. However, blocking some types of cookies may impact your
                            experience of the site and the services we are able to offer.
                        </IntroText>
                    </IntroSection>

                    <CategoriesSection>
                        {COOKIE_CATEGORIES.map((category) => (
                            <CategoryItem key={category.id}>
                                <CategoryHeader onClick={() => handleToggleExpand(category.id)}>
                                    <CategoryTitleRow>
                                        <ExpandIcon $expanded={expandedCategory === category.id}>
                                            <ChevronDown />
                                        </ExpandIcon>
                                        <CategoryTitle>{category.title}</CategoryTitle>
                                    </CategoryTitleRow>
                                    <ToggleSwitch
                                        $checked={preferences[category.id]}
                                        $disabled={category.required}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggle(category.id);
                                        }}
                                    >
                                        <ToggleKnob $checked={preferences[category.id]} />
                                        {category.required && <CheckMark>&#10003;</CheckMark>}
                                        {!category.required && !preferences[category.id] && (
                                            <CrossMark>&times;</CrossMark>
                                        )}
                                    </ToggleSwitch>
                                </CategoryHeader>
                                {expandedCategory === category.id && (
                                    <CategoryDescription>
                                        {category.description}
                                    </CategoryDescription>
                                )}
                            </CategoryItem>
                        ))}
                    </CategoriesSection>
                </ModalBody>

                <ModalFooter>
                    <FooterButtonGroup>
                        <AcceptAllButton onClick={handleAcceptAll}>Accept all</AcceptAllButton>
                        <RejectAllButton onClick={handleRejectAll}>Reject all</RejectAllButton>
                    </FooterButtonGroup>
                    <SaveButton onClick={handleSaveSettings}>Save settings</SaveButton>
                </ModalFooter>
            </ModalContainer>
        </ModalOverlay>
    );
};

// Chevron Down SVG component
const ChevronDown: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 20px;
`;

const ModalContainer = styled.div`
    background: ${theme.colors.white};
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: fadeIn 0.2s ease-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: scale(0.95);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid ${theme.colors.gray_5};
`;

const ModalTitle = styled.h2`
    font-family: ${theme.fonts.primary};
    font-size: 20px;
    font-weight: 700;
    color: ${theme.colors.black};
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        background: ${theme.colors.gray_6};
    }
`;

const CloseIcon = styled.span`
    font-size: 28px;
    color: ${theme.colors.gray_3};
    line-height: 1;
`;

const ModalBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex: 1;
`;

const IntroSection = styled.div`
    margin-bottom: 24px;
`;

const IntroTitle = styled.h3`
    font-family: ${theme.fonts.primary};
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.black};
    margin: 0 0 8px 0;
`;

const IntroText = styled.p`
    font-family: ${theme.fonts.primary};
    font-size: 14px;
    line-height: 1.6;
    color: ${theme.colors.gray_2};
    margin: 0;
`;

const CategoriesSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const CategoryItem = styled.div`
    border-bottom: 1px solid ${theme.colors.gray_5};

    &:last-child {
        border-bottom: none;
    }
`;

const CategoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    cursor: pointer;

    &:hover {
        background: ${theme.colors.gray_6};
        margin: 0 -24px;
        padding: 16px 24px;
    }
`;

const CategoryTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.gray_3};
    transition: transform 0.2s ease;
    transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0deg)")};
`;

const CategoryTitle = styled.span`
    font-family: ${theme.fonts.primary};
    font-size: 15px;
    font-weight: 500;
    color: ${theme.colors.black};
`;

const CategoryDescription = styled.p`
    font-family: ${theme.fonts.primary};
    font-size: 13px;
    line-height: 1.6;
    color: ${theme.colors.gray_2};
    margin: 0 0 16px 20px;
    padding-right: 60px;
`;

const ToggleSwitch = styled.button<{ $checked: boolean; $disabled: boolean }>`
    position: relative;
    width: 48px;
    height: 28px;
    border-radius: 14px;
    border: none;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    background: ${({ $checked, $disabled }) =>
        $disabled ? theme.colors.gray_4 : $checked ? theme.colors.highlight : theme.colors.gray_4};
    transition: background 0.2s ease;
    flex-shrink: 0;
    opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
`;

const ToggleKnob = styled.div<{ $checked: boolean }>`
    position: absolute;
    top: 4px;
    left: ${({ $checked }) => ($checked ? "24px" : "4px")};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${theme.colors.white};
    transition: left 0.2s ease;
`;

const CheckMark = styled.span`
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.white};
    font-size: 12px;
    font-weight: bold;
`;

const CrossMark = styled.span`
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.white};
    font-size: 16px;
    font-weight: bold;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-top: 1px solid ${theme.colors.gray_5};
    gap: 12px;
    flex-wrap: wrap;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
    `}
`;

const FooterButtonGroup = styled.div`
    display: flex;
    gap: 12px;

    ${breakpoint.breakOnlyMobile`
        width: 100%;
    `}
`;

const BaseButton = styled.button`
    font-family: ${theme.fonts.primary};
    font-size: 14px;
    font-weight: 600;
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: 2px solid ${theme.colors.highlight};
        outline-offset: 2px;
    }

    ${breakpoint.breakOnlyMobile`
        flex: 1;
        padding: 12px 16px;
    `}
`;

const AcceptAllButton = styled(BaseButton)`
    background: ${theme.colors.highlight};
    color: ${theme.colors.white};
    border: none;

    &:hover {
        opacity: 0.9;
    }
`;

const RejectAllButton = styled(BaseButton)`
    background: ${theme.colors.white};
    color: ${theme.colors.gray_2};
    border: 2px solid ${theme.colors.gray_5};

    &:hover {
        background: ${theme.colors.gray_6};
    }
`;

const SaveButton = styled(BaseButton)`
    background: ${theme.colors.white};
    color: ${theme.colors.black};
    border: 2px solid ${theme.colors.gray_4};

    &:hover {
        background: ${theme.colors.gray_6};
    }

    ${breakpoint.breakOnlyMobile`
        width: 100%;
    `}
`;

export default CookiePreferencesModal;
